import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock3,
  Fingerprint,
  Globe,
  Loader2,
  Lock,
  Mail,
  Package2,
  RefreshCw,
  Send,
  ShieldCheck,
  User,
} from 'lucide-react';
import {
  DEFAULT_WORKSPACE_SLUG,
  deriveCustomerSlugFromWebsite,
  markOnboardingCompleted,
  persistSetupToken,
  persistWorkspaceSlug,
  sanitizeWorkspaceSlug,
  sanitizeWorkspaceSlugInput,
  setupOrAdminAuthHeaders,
} from '../utils/workspace';
import {
  clearEmailVerificationResume,
  EmailVerificationResumeState,
  persistEmailVerificationResume,
  readEmailVerificationResume,
} from '../utils/emailVerification';

const STORAGE_KEY = 'customize-flow-inputs';
const PROGRESS_DURATION_MS = 30000;
const PROGRESS_INTERVAL_MS = Math.floor(PROGRESS_DURATION_MS / 100);
const PROGRESS_CATCH_UP_INTERVAL_MS = 30;
const PROGRESS_CATCH_UP_STEP = 4;
const PROGRESS_MAX = 100;
const PREWARM_REALTIME_TIMEOUT_MS = 2500;
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

interface CustomizeFlowProps {
  isDarkMode: boolean;
}

interface FormState {
  website: string;
  productWebsite: string;
  customerSlug: string;
  adminUsername: string;
  adminEmail: string;
  adminPassword: string;
  maxCrawlPages: number;
}

interface WorkspaceSetupResponse {
  success?: boolean;
  status?: string;
  code?: string;
  message?: string;
  customerSlug?: string;
  workspaceSlug?: string;
  agentSlug?: string;
  summary?: {
    pagesScraped?: number;
    errors?: number;
  };
  errors?: unknown[];
  profile?: unknown;
  setupToken?: string;
  adminAuth?: AdminAuthPayload | null;
  error?: string;
}

interface AdminAuthPayload {
  token?: string;
  username?: string;
  user?: {
    id?: string | null;
  };
  user_id?: string | null;
  role?: string | null;
  platform_role?: string | null;
  has_active_customeradmin?: boolean;
  active_customer_id?: string | null;
  active_customer_slug?: string | null;
  active_customer_name?: string | null;
  active_workspace_slug?: string | null;
  active_workspace_name?: string | null;
  active_agent_slug?: string | null;
  active_agent_name?: string | null;
  customer?: {
    id?: string | null;
    slug?: string | null;
    name?: string | null;
  };
  workspace?: {
    slug?: string | null;
    name?: string | null;
  };
  agent?: {
    slug?: string | null;
    name?: string | null;
  };
  customer_memberships?: Array<{
    customer_slug?: string | null;
    customer_name?: string | null;
  }>;
}

interface SetupPreflightResponse {
  success?: boolean;
  status?: string;
  code?: string;
  customerSlug?: string;
  loginUrl?: string;
  message?: string;
  error?: string;
  fieldErrors?: Record<string, string>;
}

interface EmailVerificationResponse {
  success?: boolean;
  status?: 'pending' | 'verified' | 'expired' | 'consumed';
  challengeId?: string;
  expiresAt?: string;
  email?: string;
  customerSlug?: string;
  code?: string;
  message?: string;
  error?: string;
}

type PendingEmailVerification = EmailVerificationResumeState;

type RuntimeWarmupStatus = 'idle' | 'running' | 'success' | 'partial' | 'error';

const getRealtimeWsUrl = (): string => {
  if (process.env.REACT_APP_REALTIME_WS_URL) {
    return process.env.REACT_APP_REALTIME_WS_URL;
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const isSecure = window.location.protocol === 'https:';
  const isNgrok = host.includes('ngrok');
  const useProxy = process.env.REACT_APP_USE_PROXY === 'true';

  if (isSecure || isNgrok || useProxy) {
    return `${protocol}//${host}/realtime`;
  }

  const wsHost = window.location.hostname || 'localhost';
  return `${protocol}//${wsHost}:8766`;
};

const DEFAULT_SETUP_ERROR =
  'Die Einrichtung konnte nicht abgeschlossen werden. Bitte prüfe deine Angaben und versuche es erneut.';

const DEFAULT_MAX_CRAWL_PAGES = 25;
const ADMIN_SLIDER_MAX = 500;
const SETUP_LOADING_PHRASE_INTERVAL_MS = 4600;
const EMAIL_VERIFICATION_POLL_INTERVAL_MS = 2500;

const setupStatusErrorMessage = (status: number, statusText: string): string => {
  if (status === 504) {
    return 'Die Einrichtung dauert länger als der aktuelle Server-Timeout. Der Server kann im Hintergrund trotzdem noch fertig werden; bitte prüfe gleich das Dashboard oder versuche es mit weniger Crawl-Seiten erneut.';
  }
  if (status === 502) {
    return 'Die Einrichtung ist im Backend fehlgeschlagen. Details findest du unten.';
  }
  return `Die Einrichtung ist fehlgeschlagen (${status}${statusText ? ` ${statusText}` : ''}).`;
};

const setupErrorDetailToText = (detail: unknown): string => {
  if (!detail) {
    return '';
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (typeof detail !== 'object') {
    return String(detail);
  }

  const item = detail as Record<string, unknown>;
  const sourceType = typeof item.sourceType === 'string' ? item.sourceType : '';
  const url = typeof item.url === 'string' ? item.url : '';
  const error = typeof item.error === 'string'
    ? item.error
    : typeof item.message === 'string'
      ? item.message
      : typeof item.code === 'string'
        ? item.code
        : '';

  return [sourceType, error, url].filter(Boolean).join(' - ');
};

const setupPayloadErrorMessage = (
  payload: WorkspaceSetupResponse | null,
  fallback: string
): string => {
  const baseMessage = payload?.error || payload?.message || fallback;
  const rawErrors = payload?.errors;
  const details = Array.isArray(rawErrors)
    ? rawErrors.map(setupErrorDetailToText).filter(Boolean).slice(0, 5)
    : [];

  if (!details.length) {
    return baseMessage;
  }

  return `${baseMessage}\n${details.map((detail) => `- ${detail}`).join('\n')}`;
};

const getWebsiteHostLabel = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return 'deine Website';
  }

  try {
    const parsed = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    return parsed.hostname.replace(/^www\./, '') || 'deine Website';
  } catch {
    return 'deine Website';
  }
};

const defaultFormState: FormState = {
  website: '',
  productWebsite: '',
  customerSlug: '',
  adminUsername: '',
  adminEmail: '',
  adminPassword: '',
  maxCrawlPages: DEFAULT_MAX_CRAWL_PAGES,
};

const shouldAutoReplaceCustomerSlug = (
  previousWebsite: string,
  currentCustomerSlug: string,
  touched: boolean,
): boolean => {
  const currentSlug = sanitizeWorkspaceSlugInput(currentCustomerSlug);
  if (!currentSlug || currentSlug === DEFAULT_WORKSPACE_SLUG) {
    return true;
  }

  const previousDerivedSlug = deriveCustomerSlugFromWebsite(previousWebsite);
  return !touched || currentSlug === previousDerivedSlug;
};

const sessionFromAdminAuthPayload = (data: AdminAuthPayload) => ({
  username: data.username || null,
  user_id: data.user?.id || data.user_id || null,
  role: data.role || null,
  platform_role: data.platform_role || null,
  is_superadmin: data.role === 'superadmin' || data.platform_role === 'superadmin' || data.platform_role === 'owner',
  has_active_customeradmin: Boolean(data.has_active_customeradmin || data.customer_memberships?.length),
  active_customer_id: data.active_customer_id || data.customer?.id || null,
  active_customer_slug: data.active_customer_slug || data.customer?.slug || null,
  active_customer_name: data.active_customer_name || data.customer?.name || data.customer_memberships?.[0]?.customer_name || null,
  active_workspace_slug: data.active_workspace_slug || data.workspace?.slug || null,
  active_workspace_name: data.active_workspace_name || data.workspace?.name || null,
  active_agent_slug: data.active_agent_slug || data.agent?.slug || null,
  active_agent_name: data.active_agent_name || data.agent?.name || null,
  customer_memberships: data.customer_memberships || [],
});

const persistAdminAuthPayload = (data: AdminAuthPayload | null | undefined): void => {
  if (!data?.token) {
    return;
  }

  window.sessionStorage.setItem('admin_token', data.token);
  window.sessionStorage.setItem('admin_username', data.username || '');
  window.sessionStorage.setItem('admin_session', JSON.stringify(sessionFromAdminAuthPayload(data)));
};

const safeDashboardRedirect = (value: string | null, customerSlug: string): string => {
  const fallback = `/dashboard/${sanitizeWorkspaceSlug(customerSlug)}`;
  if (!value) {
    return fallback;
  }
  const trimmed = value.trim();
  if (!trimmed.startsWith('/dashboard/')) {
    return fallback;
  }
  return trimmed;
};

const CustomizeFlow: React.FC<CustomizeFlowProps> = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<1 | 2>(1);
  const [progress, setProgress] = useState(0);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [customerSlugTouched, setCustomerSlugTouched] = useState(false);
  const isAdmin = !!sessionStorage.getItem('admin_token');
  const queryCustomerSlug = sanitizeWorkspaceSlugInput(
    searchParams.get('customer') || searchParams.get('workspace') || ''
  );
  const isRescrapeMode = searchParams.get('mode') === 'rescrape' && Boolean(queryCustomerSlug);
  const rescrapeDashboardUrl = safeDashboardRedirect(searchParams.get('redirect'), queryCustomerSlug);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [websiteError, setWebsiteError] = useState('');
  const [setupError, setSetupError] = useState('');
  const [preflightFieldErrors, setPreflightFieldErrors] = useState<Record<string, string>>({});
  const [preflightLoginUrl, setPreflightLoginUrl] = useState('');
  const [isCheckingPreflight, setIsCheckingPreflight] = useState(false);
  const [isCompletingSetup, setIsCompletingSetup] = useState(false);
  const [setupStatus, setSetupStatus] = useState<'idle' | 'running' | 'success' | 'error'>('idle');
  const [setupResponse, setSetupResponse] = useState<WorkspaceSetupResponse | null>(null);
  const [activeWorkspaceSlug, setActiveWorkspaceSlug] = useState('');
  const [runtimeWarmupStatus, setRuntimeWarmupStatus] = useState<RuntimeWarmupStatus>('idle');
  const [runtimeWarmupMessage, setRuntimeWarmupMessage] = useState('');
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(0);
  const [emailVerification, setEmailVerification] = useState<PendingEmailVerification | null>(null);
  const [emailVerificationError, setEmailVerificationError] = useState('');
  const [isRequestingVerification, setIsRequestingVerification] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const setupPromiseRef = useRef<Promise<WorkspaceSetupResponse> | null>(null);
  const finalizedSetupRef = useRef(false);
  const runtimeWarmupPromiseRef = useRef<Promise<void> | null>(null);
  const runtimeWarmupSlugRef = useRef('');
  const verificationPollInFlightRef = useRef(false);
  const verificationSetupStartedRef = useRef(false);
  const activeVerificationIdRef = useRef('');
  const currentVerificationIdentityRef = useRef({ email: '', customerSlug: '' });
  currentVerificationIdentityRef.current = {
    email: formState.adminEmail.trim().toLowerCase(),
    customerSlug: sanitizeWorkspaceSlug(formState.customerSlug),
  };
  // Prevents the save effect from overwriting localStorage with the empty default
  // state on the first render, before the restore effect has set the real values.
  const restoredRef = useRef(false);

  // Save effect declared FIRST so it runs before the restore effect on mount.
  // On the first render restoredRef is still false → we skip, avoiding the race.
  useEffect(() => {
    if (!restoredRef.current) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      website: formState.website,
      productWebsite: formState.productWebsite,
      customerSlug: formState.customerSlug,
      adminUsername: formState.adminUsername,
      adminEmail: formState.adminEmail,
    }));
  }, [formState.adminEmail, formState.adminUsername, formState.customerSlug, formState.website, formState.productWebsite]);

  // Restore effect declared SECOND — runs after save on mount, sets restoredRef
  // so all subsequent saves (triggered by the state update here) go through.
  useEffect(() => {
    restoredRef.current = true;
    if (isRescrapeMode) {
      setCustomerSlugTouched(true);
      setFormState((current) => ({
        ...current,
        customerSlug: queryCustomerSlug,
        adminPassword: '',
      }));

      const loadExistingWorkspaceProfile = async () => {
        try {
          const response = await fetch(
            `${API_BASE}/api/customer/workspace/${encodeURIComponent(queryCustomerSlug)}`,
            { headers: setupOrAdminAuthHeaders(queryCustomerSlug) },
          );
          if (!response.ok) {
            return;
          }
          const payload = await response.json().catch(() => null) as {
            website?: string | null;
            productWebsite?: string | null;
          } | null;
          if (!payload) {
            return;
          }
          setFormState((current) => ({
            ...current,
            customerSlug: queryCustomerSlug,
            website: current.website || payload.website || '',
            productWebsite: current.productWebsite || payload.productWebsite || '',
          }));
        } catch {
          // Existing values are only a convenience for the rescrape form.
        }
      };

      void loadExistingWorkspaceProfile();
      return;
    }

    const saved = window.localStorage.getItem(STORAGE_KEY);
    let restoredState = { ...defaultFormState };
    let restoredCustomerSlugIsManual = false;
    try {
      const parsed = saved ? JSON.parse(saved) as Partial<FormState> : {};
      const restoredWebsite = typeof parsed.website === 'string' ? parsed.website : '';
      const restoredCustomerSlug = typeof parsed.customerSlug === 'string'
        ? sanitizeWorkspaceSlugInput(parsed.customerSlug)
        : '';
      const derivedCustomerSlug = deriveCustomerSlugFromWebsite(restoredWebsite);
      restoredCustomerSlugIsManual = Boolean(restoredCustomerSlug) && restoredCustomerSlug !== derivedCustomerSlug;
      restoredState = {
        website: restoredWebsite,
        productWebsite: parsed.productWebsite ?? '',
        customerSlug: restoredCustomerSlug || derivedCustomerSlug,
        adminUsername: typeof parsed.adminUsername === 'string' ? parsed.adminUsername : '',
        adminEmail: typeof parsed.adminEmail === 'string' ? parsed.adminEmail : '',
        adminPassword: '',
        maxCrawlPages: DEFAULT_MAX_CRAWL_PAGES,
      };
    } catch {
      window.localStorage.removeItem(STORAGE_KEY);
    }

    const verificationResume = readEmailVerificationResume();
    if (verificationResume) {
      restoredState.adminEmail = verificationResume.email;
      restoredState.customerSlug = verificationResume.customerSlug;
      restoredCustomerSlugIsManual = true;
      activeVerificationIdRef.current = verificationResume.challengeId;
      setEmailVerification(verificationResume);
    }
    setFormState(restoredState);
    setCustomerSlugTouched(restoredCustomerSlugIsManual);
  }, [isRescrapeMode, queryCustomerSlug]);

  useEffect(() => {
    if (step !== 2 || progress >= PROGRESS_MAX) {
      return;
    }

    const intervalMs = setupStatus === 'success' ? PROGRESS_CATCH_UP_INTERVAL_MS : PROGRESS_INTERVAL_MS;
    const progressStep = setupStatus === 'success' ? PROGRESS_CATCH_UP_STEP : 1;
    const progressCap = setupStatus === 'success' ? PROGRESS_MAX : 95;

    const timer = window.setInterval(() => {
      setProgress((currentProgress) => {
        if (currentProgress >= PROGRESS_MAX) {
          window.clearInterval(timer);
          return currentProgress;
        }
        return Math.min(currentProgress + progressStep, progressCap);
      });
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [step, progress, setupStatus]);

  const stages = useMemo(() => {
    return [
      {
        label: 'Die KI-Agentin analysiert deinen Auftritt...',
        status: progress >= 33 ? 'complete' : 'active',
      },
      {
        label: 'Die KI-Agentin erfasst dein Angebot...',
        status: progress >= 66 ? 'complete' : progress >= 33 ? 'active' : 'pending',
      },
      {
        label: 'Die KI-Agentin stimmt Stil und Tonalität ab...',
        status: progress >= 100 ? 'complete' : progress >= 66 ? 'active' : 'pending',
      },
    ] as const;
  }, [progress]);

  const setupLoadingPhrases = useMemo(() => {
    const websiteLabel = getWebsiteHostLabel(formState.website);
    return [
      `${websiteLabel} wird gerade gelesen.`,
      'Agenten werden gebrieft.',
      'Angebote, Sprache und Kontaktwege werden sortiert.',
      'Der erste Prompt-Entwurf nimmt Form an.',
      'Sally lernt, wie dein Unternehmen klingt.',
      'Dashboard und Admin-Bereich werden vorbereitet.',
    ];
  }, [formState.website]);

  useEffect(() => {
    if (step !== 2) {
      setLoadingPhraseIndex(0);
      return;
    }

    const phraseInterval = window.setInterval(() => {
      setLoadingPhraseIndex((currentIndex) => (currentIndex + 1) % setupLoadingPhrases.length);
    }, SETUP_LOADING_PHRASE_INTERVAL_MS);

    return () => window.clearInterval(phraseInterval);
  }, [setupLoadingPhrases.length, step]);

  const prewarmRealtimeSocket = async (): Promise<void> => {
    const wsUrl = getRealtimeWsUrl();

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const ws = new WebSocket(wsUrl);

      const timeoutId = window.setTimeout(() => {
        finish(new Error('realtime_prewarm_timeout'));
      }, PREWARM_REALTIME_TIMEOUT_MS);

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        ws.onopen = null;
        ws.onerror = null;
        ws.onclose = null;
      };

      const finish = (error?: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanup();
        try {
          ws.close();
        } catch {
          // Ignore close errors during prewarm.
        }

        if (error) {
          reject(error);
          return;
        }
        resolve();
      };

      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({ type: 'ping' }));
        } catch {
          // Ping is optional during prewarm.
        }
        finish();
      };

      ws.onerror = () => {
        finish(new Error('realtime_prewarm_failed'));
      };

      ws.onclose = () => {
        if (!settled) {
          finish(new Error('realtime_prewarm_closed'));
        }
      };
    });
  };

  const prewarmWorkspaceReads = async (workspaceSlug: string): Promise<void> => {
    const slug = encodeURIComponent(workspaceSlug);
    const headers = setupOrAdminAuthHeaders(workspaceSlug);
    await Promise.all([
      fetch(`${API_BASE}/api/customer/workspace/${slug}`, { headers }),
      fetch(`${API_BASE}/api/customer/workspace/${slug}/scraped-content`, { headers }),
    ]);
  };

  const startRuntimeWarmup = (workspaceSlug: string): Promise<void> => {
    const trimmedSlug = workspaceSlug.trim();
    if (!trimmedSlug) {
      return Promise.resolve();
    }

    if (
      runtimeWarmupSlugRef.current === trimmedSlug &&
      (runtimeWarmupStatus === 'success' || runtimeWarmupStatus === 'partial')
    ) {
      return Promise.resolve();
    }

    if (runtimeWarmupPromiseRef.current && runtimeWarmupSlugRef.current === trimmedSlug) {
      return runtimeWarmupPromiseRef.current;
    }

    runtimeWarmupSlugRef.current = trimmedSlug;
    setRuntimeWarmupStatus('running');
    setRuntimeWarmupMessage('Verbindungen und Workspace-Daten werden vorbereitet...');

    const warmupTasks: Array<{ name: string; run: () => Promise<unknown> }> = [
      {
        name: 'backend_health',
        run: () => fetch(`${API_BASE}/health`),
      },
      {
        name: 'workspace_data',
        run: () => prewarmWorkspaceReads(trimmedSlug),
      },
      {
        name: 'realtime_socket',
        run: () => prewarmRealtimeSocket(),
      },
    ];

    const warmupPromise = Promise.allSettled(
      warmupTasks.map(async (task) => {
        try {
          await task.run();
          return true;
        } catch (error) {
          console.warn(`[SetupWarmup] ${task.name} failed`, error);
          return false;
        }
      })
    )
      .then((results) => {
        const successfulTasks = results.filter((result) => result.status === 'fulfilled' && result.value).length;

        if (successfulTasks === warmupTasks.length) {
          setRuntimeWarmupStatus('success');
          setRuntimeWarmupMessage('Avatar und Verbindungen sind vorbereitet.');
          return;
        }

        if (successfulTasks > 0) {
          setRuntimeWarmupStatus('partial');
          setRuntimeWarmupMessage('Setup ist fertig. Einige Verbindungen werden beim ersten Start ergänzt.');
          return;
        }

        setRuntimeWarmupStatus('error');
        setRuntimeWarmupMessage('Setup ist fertig. Verbindungen werden beim ersten Start aufgebaut.');
      })
      .finally(() => {
        if (runtimeWarmupPromiseRef.current === warmupPromise) {
          runtimeWarmupPromiseRef.current = null;
        }
      });

    runtimeWarmupPromiseRef.current = warmupPromise;
    return warmupPromise;
  };

  const runSetupPreflight = async (
    customerSlug: string,
    website: string,
    productWebsite: string
  ): Promise<SetupPreflightResponse> => {
    const adminToken = sessionStorage.getItem('admin_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (adminToken) {
      headers['Authorization'] = `Bearer ${adminToken}`;
    }

    const body: Record<string, unknown> = {
      workspaceSlug: customerSlug,
      customerSlug,
      website,
      productWebsite,
    };
    if (!isRescrapeMode) {
      body.ownerUsername = formState.adminUsername.trim();
      body.ownerEmail = formState.adminEmail.trim();
      body.ownerPassword = formState.adminPassword;
    }

    const response = await fetch(`${API_BASE}/api/customer/workspace/setup/preflight`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    const payload = (await response.json().catch(() => null)) as SetupPreflightResponse | null;
    if (!response.ok) {
      return payload || {
        success: false,
        status: 'error',
        code: 'preflight_failed',
        error: DEFAULT_SETUP_ERROR,
      };
    }

    return payload || { success: true, status: 'available', customerSlug };
  };

  const runWorkspaceSetup = async (
    customerSlug: string,
    website: string,
    productWebsite: string,
    emailVerificationId?: string
  ): Promise<WorkspaceSetupResponse> => {
    const adminToken = sessionStorage.getItem('admin_token');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (adminToken) {
      headers['Authorization'] = `Bearer ${adminToken}`;
    }

    const body: Record<string, unknown> = {
      workspaceSlug: customerSlug,
      customerSlug,
      website,
      productWebsite,
      use_llm: true,
    };
    if (!isRescrapeMode) {
      body.ownerUsername = formState.adminUsername.trim();
      body.ownerEmail = formState.adminEmail.trim();
      body.ownerPassword = formState.adminPassword;
      body.emailVerificationId = emailVerificationId;
    }
    if (adminToken && formState.maxCrawlPages !== DEFAULT_MAX_CRAWL_PAGES) {
      body['maxCrawlPages'] = Math.max(1, Math.floor(formState.maxCrawlPages));
    }

    const response = await fetch(`${API_BASE}/api/customer/workspace/setup`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    let payload: WorkspaceSetupResponse | null = null;
    try {
      payload = (await response.json()) as WorkspaceSetupResponse;
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const message = setupPayloadErrorMessage(
        payload,
        setupStatusErrorMessage(response.status, response.statusText)
      );
      console.error('Workspace setup failed:', {
        status: response.status,
        statusText: response.statusText,
        payload,
        customerSlug,
        website,
        productWebsite,
      });
      throw new Error(message);
    }

    if (payload?.success === false) {
      const message = setupPayloadErrorMessage(payload, DEFAULT_SETUP_ERROR);
      console.error('Workspace setup returned failure:', {
        payload,
        customerSlug,
        website,
        productWebsite,
      });
      throw new Error(message);
    }

    return payload ?? {};
  };

  const startWorkspaceSetup = (
    customerSlug: string,
    website: string,
    productWebsite: string,
    options?: { forceRefresh?: boolean; emailVerificationId?: string }
  ): Promise<WorkspaceSetupResponse> => {
    const forceRefresh = Boolean(options?.forceRefresh);

    if (
      !forceRefresh &&
      customerSlug === activeWorkspaceSlug &&
      setupStatus === 'running' &&
      setupPromiseRef.current
    ) {
      return setupPromiseRef.current;
    }

    if (
      !forceRefresh &&
      customerSlug === activeWorkspaceSlug &&
      setupStatus === 'success' &&
      setupResponse
    ) {
      return Promise.resolve(setupResponse);
    }

    setSetupStatus('running');
    setSetupError('');
    setSetupResponse(null);
    setActiveWorkspaceSlug(customerSlug);

    const setupPromise = runWorkspaceSetup(
      customerSlug,
      website,
      productWebsite,
      options?.emailVerificationId,
    )
      .then((response) => {
        if (!isRescrapeMode) {
          clearEmailVerificationResume();
        }
        setSetupStatus('success');
        setSetupResponse(response);
        void startRuntimeWarmup(customerSlug);
        return response;
      })
      .catch((error) => {
        const message =
          error instanceof Error && error.message.trim() ? error.message.trim() : DEFAULT_SETUP_ERROR;
        setSetupStatus('error');
        setSetupError(message === 'setup_failed' ? DEFAULT_SETUP_ERROR : message);
        throw error;
      })
      .finally(() => {
        if (setupPromiseRef.current === setupPromise) {
          setupPromiseRef.current = null;
        }
      });

    setupPromiseRef.current = setupPromise;
    return setupPromise;
  };

  const prepareAndStartWorkspaceSetup = (
    customerSlug: string,
    website: string,
    productWebsite: string,
    emailVerificationId?: string,
  ) => {
    if (!isRescrapeMode && verificationSetupStartedRef.current) {
      return;
    }
    if (!isRescrapeMode) {
      verificationSetupStartedRef.current = true;
    }

    setWebsiteError('');
    setSetupError('');
    setProgress(0);
    setStep(2);
    setSetupResponse(null);
    setSetupStatus('idle');
    setRuntimeWarmupStatus('idle');
    setRuntimeWarmupMessage('');
    setLoadingPhraseIndex(0);
    finalizedSetupRef.current = false;

    void startWorkspaceSetup(customerSlug, website, productWebsite, {
      forceRefresh: true,
      emailVerificationId,
    });
  };

  const requestEmailVerification = async (
    email: string,
    customerSlug: string,
  ): Promise<PendingEmailVerification> => {
    const response = await fetch(`${API_BASE}/api/customer/workspace/setup/email-verification/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, customerSlug }),
    });
    const payload = (await response.json().catch(() => null)) as EmailVerificationResponse | null;

    if (!response.ok || !payload?.success || !payload.challengeId) {
      throw new Error(
        payload?.error || payload?.message || 'Der Bestätigungslink konnte nicht gesendet werden. Bitte versuche es erneut.',
      );
    }

    return {
      challengeId: payload.challengeId,
      email: payload.email || email,
      customerSlug,
      expiresAt: payload.expiresAt || '',
      status: 'pending',
    };
  };

  const checkEmailVerification = async (challenge: PendingEmailVerification, manual = false) => {
    if (verificationPollInFlightRef.current || challenge.status !== 'pending') {
      return;
    }

    verificationPollInFlightRef.current = true;
    if (manual) {
      setIsCheckingVerification(true);
      setEmailVerificationError('');
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/customer/workspace/setup/email-verification/status/${encodeURIComponent(challenge.challengeId)}`,
      );
      const payload = (await response.json().catch(() => null)) as EmailVerificationResponse | null;
      if (!response.ok || !payload?.success || !payload.status) {
        throw new Error(payload?.error || payload?.message || 'Der Bestätigungsstatus konnte nicht geprüft werden.');
      }

      const currentIdentity = currentVerificationIdentityRef.current;
      if (
        activeVerificationIdRef.current !== challenge.challengeId
        || currentIdentity.email !== challenge.email.trim().toLowerCase()
        || currentIdentity.customerSlug !== challenge.customerSlug
      ) {
        return;
      }

      const updatedChallenge: PendingEmailVerification = {
        ...challenge,
        status: payload.status,
        expiresAt: payload.expiresAt || challenge.expiresAt,
      };
      persistEmailVerificationResume(updatedChallenge);
      setEmailVerification((current) => {
        if (!current || current.challengeId !== challenge.challengeId) {
          return current;
        }
        if (
          current.status === updatedChallenge.status
          && current.expiresAt === updatedChallenge.expiresAt
        ) {
          return current;
        }
        return updatedChallenge;
      });

      if (payload.status === 'verified') {
        setEmailVerificationError('');
        if (
          formState.adminPassword.length >= 8
          && formState.adminUsername.trim()
          && formState.website.trim()
        ) {
          prepareAndStartWorkspaceSetup(
            challenge.customerSlug,
            formState.website.trim(),
            formState.productWebsite.trim(),
            challenge.challengeId,
          );
        }
      } else if (payload.status === 'expired') {
        setEmailVerificationError('Der Bestätigungslink ist abgelaufen. Fordere bitte einen neuen Link an.');
      } else if (payload.status === 'consumed') {
        setEmailVerificationError('Dieser Bestätigungslink wurde bereits verwendet. Fordere bei Bedarf einen neuen Link an.');
      }
    } catch (error) {
      if (manual) {
        setEmailVerificationError(
          error instanceof Error && error.message
            ? error.message
            : 'Der Bestätigungsstatus konnte nicht geprüft werden.',
        );
      }
    } finally {
      verificationPollInFlightRef.current = false;
      if (manual) {
        setIsCheckingVerification(false);
      }
    }
  };

  useEffect(() => {
    if (!emailVerification || emailVerification.status !== 'pending' || step !== 1) {
      return;
    }

    void checkEmailVerification(emailVerification);
    const timer = window.setInterval(() => {
      void checkEmailVerification(emailVerification);
    }, EMAIL_VERIFICATION_POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  });

  useEffect(() => {
    if (!emailVerification || step !== 1) {
      return;
    }

    const currentEmail = formState.adminEmail.trim().toLowerCase();
    const currentSlug = sanitizeWorkspaceSlug(formState.customerSlug);
    if (
      currentEmail !== emailVerification.email.trim().toLowerCase()
      || currentSlug !== emailVerification.customerSlug
    ) {
      setEmailVerification(null);
      setEmailVerificationError('');
      activeVerificationIdRef.current = '';
      verificationSetupStartedRef.current = false;
      clearEmailVerificationResume();
    }
  }, [emailVerification, formState.adminEmail, formState.customerSlug, step]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const website = formState.website.trim();
    const productWebsite = formState.productWebsite.trim();
    const customerSlug = sanitizeWorkspaceSlug(formState.customerSlug || deriveCustomerSlugFromWebsite(website));
    const nextFieldErrors: Record<string, string> = {};
    setPreflightFieldErrors({});
    setPreflightLoginUrl('');
    setSetupError('');

    if (!website) {
      nextFieldErrors.website = 'Bitte gib eine Website ein, um fortzufahren.';
    }
    if (!customerSlug || customerSlug === 'customer-workspace') {
      nextFieldErrors.customerSlug = 'Bitte gib eine Customer-ID ein.';
    }
    if (isRescrapeMode && !isAdmin) {
      nextFieldErrors.auth = 'Bitte öffne den erneuten Scrape aus deinem eingeloggten Dashboard.';
    }
    if (!isRescrapeMode && !formState.adminUsername.trim()) {
      nextFieldErrors.ownerUsername = 'Bitte gib einen Admin Username ein.';
    }
    if (!isRescrapeMode && !formState.adminEmail.trim()) {
      nextFieldErrors.ownerEmail = 'Bitte gib eine Admin E-Mail ein.';
    } else if (!isRescrapeMode && !formState.adminEmail.includes('@')) {
      nextFieldErrors.ownerEmail = 'Bitte gib eine gültige E-Mail-Adresse ein.';
    }
    if (!isRescrapeMode && !formState.adminPassword) {
      nextFieldErrors.ownerPassword = 'Bitte gib ein Admin Passwort ein.';
    } else if (!isRescrapeMode && formState.adminPassword.length < 8) {
      nextFieldErrors.ownerPassword = 'Das Passwort muss mindestens 8 Zeichen lang sein.';
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setPreflightFieldErrors(nextFieldErrors);
      setWebsiteError(nextFieldErrors.website || nextFieldErrors.auth || 'Bitte prüfe die markierten Angaben.');
      return;
    }

    setWebsiteError('');
    setIsCheckingPreflight(true);
    try {
      const preflight = await runSetupPreflight(customerSlug, website, productWebsite);
      const responseCustomerSlug = sanitizeWorkspaceSlug(preflight.customerSlug || customerSlug);
      if (!preflight.success) {
        const fieldErrors = preflight.fieldErrors || {};
        setPreflightFieldErrors(fieldErrors);
        if (preflight.code === 'customer_slug_exists' || preflight.status === 'customer_exists') {
          setSetupError('Diese Customer-ID existiert bereits. Bitte logge dich ein, um das Dashboard zu öffnen.');
          setPreflightLoginUrl(preflight.loginUrl || `/login?customer=${responseCustomerSlug}&redirect=/dashboard/${responseCustomerSlug}`);
          return;
        }
        setWebsiteError(fieldErrors.website || preflight.error || preflight.message || DEFAULT_SETUP_ERROR);
        return;
      }

      setFormState((currentState) => ({
        ...currentState,
        customerSlug: responseCustomerSlug,
      }));

      if (isRescrapeMode) {
        prepareAndStartWorkspaceSetup(responseCustomerSlug, website, productWebsite);
        return;
      }

      const normalizedEmail = formState.adminEmail.trim().toLowerCase();
      if (
        emailVerification?.status === 'verified'
        && emailVerification.email.trim().toLowerCase() === normalizedEmail
        && emailVerification.customerSlug === responseCustomerSlug
      ) {
        prepareAndStartWorkspaceSetup(
          responseCustomerSlug,
          website,
          productWebsite,
          emailVerification.challengeId,
        );
        return;
      }

      setIsRequestingVerification(true);
      setEmailVerificationError('');
      verificationSetupStartedRef.current = false;
      const verification = await requestEmailVerification(
        formState.adminEmail.trim(),
        responseCustomerSlug,
      );
      activeVerificationIdRef.current = verification.challengeId;
      persistEmailVerificationResume(verification);
      setEmailVerification(verification);
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : DEFAULT_SETUP_ERROR;
      if (!isRescrapeMode) {
        setEmailVerificationError(message);
      } else {
        setWebsiteError(message);
      }
    } finally {
      setIsCheckingPreflight(false);
      setIsRequestingVerification(false);
    }
  };

  const handleExistingCustomerLogin = () => {
    if (!preflightLoginUrl) {
      return;
    }
    navigate(preflightLoginUrl);
  };

  const cardClasses = isDarkMode
    ? 'border border-white/10 bg-slate-900/90 text-slate-50 shadow-[0_32px_80px_rgba(15,23,42,0.55)]'
    : 'border border-slate-200 bg-white/94 text-slate-900 shadow-[0_32px_80px_rgba(15,23,42,0.12)]';

  const mutedTextClasses = isDarkMode ? 'text-slate-300' : 'text-slate-500';
  const inputClasses = isDarkMode
    ? 'border border-slate-700 bg-slate-950/70 text-white placeholder:text-slate-500 focus:border-blue-400'
    : 'border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-blue-500';
  const isProgressComplete = progress >= PROGRESS_MAX;
  const isSetupReady = isProgressComplete && setupStatus === 'success';
  const isWaitingForSetup = isProgressComplete && setupStatus === 'running';

  useEffect(() => {
    if (!isSetupReady || !setupResponse || finalizedSetupRef.current) {
      return;
    }

    finalizedSetupRef.current = true;
    setIsCompletingSetup(true);

    const website = formState.website.trim();
    const productWebsite = formState.productWebsite.trim();
    const responseCustomerSlug = sanitizeWorkspaceSlug(
      setupResponse.customerSlug || setupResponse.workspaceSlug || formState.customerSlug
    );
    const persistedWorkspaceSlug = persistWorkspaceSlug(responseCustomerSlug);
    persistSetupToken(responseCustomerSlug, setupResponse.setupToken);
    persistAdminAuthPayload(setupResponse.adminAuth);
    markOnboardingCompleted();

    const timer = window.setTimeout(() => {
      navigate(isRescrapeMode ? rescrapeDashboardUrl : `/dashboard/${persistedWorkspaceSlug}`, {
        replace: true,
        state: {
          customizeFlow: {
            website,
            productWebsite,
            customerSlug: persistedWorkspaceSlug,
          },
          workspaceSlug: persistedWorkspaceSlug,
          workspaceSetup: setupResponse,
        },
      });
    }, 650);

    return () => window.clearTimeout(timer);
  }, [
    formState.customerSlug,
    formState.productWebsite,
    formState.website,
    isSetupReady,
    isRescrapeMode,
    navigate,
    rescrapeDashboardUrl,
    setupResponse,
  ]);

  return (
    <div
      className={`customize-page min-h-screen px-4 py-10 sm:px-6 lg:px-8 ${
        isDarkMode ? 'bg-slate-950' : 'bg-gray-100'
      }`}
    >
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-6xl items-center justify-center">
        <div
          className={`w-full max-w-2xl rounded-[32px] p-8 sm:p-10 ${cardClasses}`}
        >
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {[1, 2].map((item) => {
                const active = item === step;
                const complete = item < step;

                return (
                  <React.Fragment key={item}>
                    <div
                      className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
                        complete || active
                          ? 'bg-gradient-to-r from-slate-900 to-blue-600 text-white'
                          : isDarkMode
                            ? 'bg-slate-800 text-slate-400'
                            : 'bg-slate-100 text-slate-400'
                      }`}
                    >
                      {item}
                    </div>
                    {item === 1 && (
                      <div
                        className={`h-px w-12 ${
                          step === 2
                            ? 'bg-gradient-to-r from-slate-700 to-blue-500'
                            : isDarkMode
                              ? 'bg-slate-700'
                              : 'bg-orange-100'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              {isRescrapeMode ? (
                <button
                  type="button"
                  onClick={() => navigate(rescrapeDashboardUrl)}
                  className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-xs font-semibold transition ${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Abbrechen
                </button>
              ) : null}
              <p className={`text-sm ${mutedTextClasses}`}>Schritt {step} von 2</p>
            </div>
          </div>

          {step === 1 ? (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold tracking-tight">
                  {isRescrapeMode ? 'Scraping erneut starten' : 'Konto Einrichtung'}
                </h1>
                <p className={`text-sm leading-6 ${mutedTextClasses}`}>
                  {isRescrapeMode
                    ? 'Aktualisiere die Website-Daten für deinen bestehenden Sally-Setup.'
                    : 'Gib deine Website ein. Optional kannst du zusätzlich die Website deines Produkts angeben.'}
                </p>
              </div>

              <label className="block space-y-2">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Globe className="h-4 w-4 text-blue-500" />
                  Website URL
                  <span className="text-red-500">*</span>
                </span>
                <input
                  type="text"
                  value={formState.website}
                  onChange={(event) =>
                    setFormState((currentState) => {
                      const website = event.target.value;
                      const shouldReplaceSlug = shouldAutoReplaceCustomerSlug(
                        currentState.website,
                        currentState.customerSlug,
                        customerSlugTouched,
                      );
                      return {
                        ...currentState,
                        website,
                        customerSlug: shouldReplaceSlug
                          ? deriveCustomerSlugFromWebsite(website)
                          : currentState.customerSlug,
                      };
                    })
                  }
                  placeholder="https://www.deine-website.de"
                  className={`w-full rounded-2xl px-4 py-3 text-sm outline-none transition ${inputClasses}`}
                />
                {preflightFieldErrors.website ? (
                  <p className="text-sm text-red-500">{preflightFieldErrors.website}</p>
                ) : websiteError ? (
                  <p className="text-sm text-red-500">{websiteError}</p>
                ) : null}
              </label>

              <label className="block space-y-2">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Fingerprint className="h-4 w-4 text-blue-500" />
                  Customer-ID / URL-Kürzel
                  <span className="text-red-500">*</span>
                </span>
                <input
                  type="text"
                  value={formState.customerSlug}
                  onChange={(event) => {
                    setCustomerSlugTouched(true);
                    setFormState((currentState) => ({
                      ...currentState,
                      customerSlug: sanitizeWorkspaceSlugInput(event.target.value),
                    }));
                  }}
                  placeholder="teamify"
                  className={`w-full rounded-2xl px-4 py-3 text-sm outline-none transition ${inputClasses}`}
                  required
                />
                <p className={`text-xs ${mutedTextClasses}`}>
                  Wird für Speicherort, Dashboard und Agent-Link verwendet, z. B. /dashboard/teamify.
                </p>
                {preflightFieldErrors.customerSlug ? (
                  <p className="text-sm text-red-500">{preflightFieldErrors.customerSlug}</p>
                ) : null}
              </label>

              <label className="block space-y-2">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Package2 className="h-4 w-4 text-blue-500" />
                  Produkt-Website
                  <span className={mutedTextClasses}>(optional)</span>
                </span>
                <input
                  type="text"
                  value={formState.productWebsite}
                  onChange={(event) =>
                    setFormState((currentState) => ({
                      ...currentState,
                      productWebsite: event.target.value,
                    }))
                  }
                  placeholder="https://www.deine-website.de/produkt"
                  className={`w-full rounded-2xl px-4 py-3 text-sm outline-none transition ${inputClasses}`}
                />
              </label>

              {!isRescrapeMode ? (
              <div className={`rounded-2xl border px-4 py-4 ${isDarkMode ? 'border-slate-700 bg-slate-950/35' : 'border-slate-200 bg-slate-50'}`}>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className="block space-y-2">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4 text-blue-500" />
                      Admin Username
                      <span className="text-red-500">*</span>
                    </span>
                    <input
                      type="text"
                      value={formState.adminUsername}
                      onChange={(event) =>
                        setFormState((currentState) => ({
                          ...currentState,
                          adminUsername: event.target.value,
                        }))
                      }
                      autoComplete="username"
                      className={`w-full rounded-2xl px-4 py-3 text-sm outline-none transition ${inputClasses}`}
                      required
                    />
                    {preflightFieldErrors.ownerUsername ? (
                      <p className="text-sm text-red-500">{preflightFieldErrors.ownerUsername}</p>
                    ) : null}
                  </label>

                  <label className="block space-y-2">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Mail className="h-4 w-4 text-blue-500" />
                      Admin E-Mail
                      <span className="text-red-500">*</span>
                    </span>
                    <input
                      type="email"
                      value={formState.adminEmail}
                      onChange={(event) =>
                        setFormState((currentState) => ({
                          ...currentState,
                          adminEmail: event.target.value,
                        }))
                      }
                      autoComplete="email"
                      className={`w-full rounded-2xl px-4 py-3 text-sm outline-none transition ${inputClasses}`}
                      required
                    />
                    {preflightFieldErrors.ownerEmail ? (
                      <p className="text-sm text-red-500">{preflightFieldErrors.ownerEmail}</p>
                    ) : null}
                  </label>
                </div>

                <label className="mt-4 block space-y-2">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Lock className="h-4 w-4 text-blue-500" />
                    Admin Passwort
                    <span className="text-red-500">*</span>
                  </span>
                  <input
                    type="password"
                    value={formState.adminPassword}
                    onChange={(event) =>
                      setFormState((currentState) => ({
                        ...currentState,
                        adminPassword: event.target.value,
                      }))
                    }
                    autoComplete="new-password"
                    className={`w-full rounded-2xl px-4 py-3 text-sm outline-none transition ${inputClasses}`}
                    required
                  />
                  <p className={`text-xs ${mutedTextClasses}`}>Mindestens 8 Zeichen.</p>
                  {preflightFieldErrors.ownerPassword ? (
                    <p className="text-sm text-red-500">{preflightFieldErrors.ownerPassword}</p>
                  ) : null}
                </label>
              </div>
              ) : null}

              {setupError ? (
                <div className={`whitespace-pre-line rounded-2xl border px-4 py-4 text-sm ${isDarkMode ? 'border-red-500/30 bg-red-500/10 text-red-100' : 'border-red-200 bg-red-50 text-red-700'}`}>
                  <p>{setupError}</p>
                  {preflightLoginUrl ? (
                    <button
                      type="button"
                      onClick={handleExistingCustomerLogin}
                      className="mt-3 inline-flex items-center justify-center rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      Einloggen und Dashboard öffnen
                    </button>
                  ) : null}
                </div>
              ) : null}

              {!isRescrapeMode && emailVerification ? (
                <section
                  className={`overflow-hidden rounded-2xl border ${
                    isDarkMode
                      ? 'border-blue-400/25 bg-blue-500/10'
                      : 'border-blue-200 bg-blue-50/80'
                  }`}
                  aria-labelledby="email-verification-title"
                >
                  <div className="flex gap-4 px-5 py-5">
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${isDarkMode ? 'bg-blue-400/15 text-blue-200' : 'bg-white text-blue-700 shadow-sm'}`}>
                      <ShieldCheck className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                        E-Mail-Prüfung
                      </p>
                      <h2 id="email-verification-title" className="mt-1 text-lg font-semibold">
                        {emailVerification.status === 'pending'
                          ? 'Bestätigungslink gesendet'
                          : emailVerification.status === 'verified'
                            ? 'E-Mail-Adresse bestätigt'
                            : 'Neuer Bestätigungslink erforderlich'}
                      </h2>
                      <p className={`mt-2 break-words text-sm leading-6 ${mutedTextClasses}`}>
                        {emailVerification.status === 'verified' ? (
                          <>
                            Die Adresse <strong className={isDarkMode ? 'text-white' : 'text-slate-900'}>{emailVerification.email}</strong> ist bestätigt.
                            Ergänze gegebenenfalls die übrigen Angaben, gib dein Passwort ein und starte anschließend das Setup.
                          </>
                        ) : (
                          <>
                            Öffne den Link in der E-Mail an <strong className={isDarkMode ? 'text-white' : 'text-slate-900'}>{emailVerification.email}</strong>.
                            Sobald die Adresse bestätigt ist, startet das Setup hier automatisch, sofern das Passwort noch eingegeben ist.
                          </>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className={`grid grid-cols-3 border-y px-4 py-4 ${isDarkMode ? 'border-blue-300/10 bg-slate-950/20' : 'border-blue-100 bg-white/60'}`} aria-label="Ablauf der Bestätigung">
                    {[
                      { label: 'Adresse erfasst', icon: Mail, complete: true },
                      { label: 'Inbox bestätigt', icon: ShieldCheck, complete: emailVerification.status === 'verified' },
                      { label: 'Setup startet', icon: Send, complete: false },
                    ].map((item, index) => {
                      const Icon = item.icon;
                      const active = index === 1 && emailVerification.status === 'pending';
                      return (
                        <div key={item.label} className="relative flex flex-col items-center gap-2 px-1 text-center">
                          {index < 2 ? (
                            <span className={`absolute left-[calc(50%+18px)] right-[calc(-50%+18px)] top-4 h-px ${isDarkMode ? 'bg-slate-700' : 'bg-blue-200'}`} aria-hidden="true" />
                          ) : null}
                          <span className={`relative z-10 flex h-8 w-8 items-center justify-center rounded-full border ${
                            item.complete
                              ? 'border-emerald-500 bg-emerald-500 text-white'
                              : active
                                ? isDarkMode ? 'border-blue-300 bg-blue-400/20 text-blue-100' : 'border-blue-500 bg-white text-blue-700'
                                : isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-500' : 'border-slate-200 bg-white text-slate-400'
                          }`}>
                            {active ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Icon className="h-4 w-4" aria-hidden="true" />}
                          </span>
                          <span className={`text-[11px] font-medium leading-4 ${index === 0 || active ? (isDarkMode ? 'text-slate-200' : 'text-slate-700') : mutedTextClasses}`}>
                            {item.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className={`flex items-center gap-2 text-xs ${mutedTextClasses}`} aria-live="polite">
                      <Clock3 className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {emailVerification.status === 'pending'
                        ? 'Wir prüfen den Status automatisch.'
                        : emailVerification.status === 'verified'
                          ? 'Die Bestätigung ist für diese Registrierung gespeichert.'
                          : 'Der Link ist nicht mehr gültig.'}
                    </div>
                    <button
                      type="button"
                      onClick={() => void checkEmailVerification(emailVerification, true)}
                      disabled={isCheckingVerification || emailVerification.status !== 'pending'}
                      className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                        isDarkMode
                          ? 'border-blue-300/25 bg-slate-900 text-blue-100 hover:bg-slate-800 focus:ring-offset-slate-900'
                          : 'border-blue-200 bg-white text-blue-800 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      <RefreshCw className={`h-4 w-4 ${isCheckingVerification ? 'animate-spin' : ''}`} aria-hidden="true" />
                      {isCheckingVerification ? 'Status wird geprüft' : 'Jetzt prüfen'}
                    </button>
                  </div>
                </section>
              ) : null}

              {emailVerificationError ? (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm ${isDarkMode ? 'border-red-500/30 bg-red-500/10 text-red-100' : 'border-red-200 bg-red-50 text-red-700'}`}
                  role="status"
                  aria-live="polite"
                >
                  {emailVerificationError}
                </div>
              ) : null}

              {isAdmin && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdminSettings((v) => !v)}
                    className={`flex items-center gap-1.5 text-xs font-medium transition ${isDarkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                  >
                    <span>{showAdminSettings ? '▾' : '▸'}</span>
                    Erweiterte Einstellungen
                  </button>

                  {showAdminSettings && (
                    <div className={`mt-3 rounded-2xl border px-4 py-4 space-y-3 ${isDarkMode ? 'border-amber-500/30 bg-amber-500/5' : 'border-amber-300 bg-amber-50'}`}>
                      <label className="block space-y-2">
                        <span className="text-sm font-medium">Max. Seiten crawlen</span>
                        <div className="flex items-center gap-3">
                          <input
                            type="range"
                            min={1}
                            max={ADMIN_SLIDER_MAX}
                            step={25}
                            value={Math.min(formState.maxCrawlPages, ADMIN_SLIDER_MAX)}
                            onChange={(event) =>
                              setFormState((currentState) => ({
                                ...currentState,
                                maxCrawlPages: Number(event.target.value),
                              }))
                            }
                            className="flex-1 accent-amber-500"
                          />
                          <input
                            type="number"
                            min={1}
                            value={formState.maxCrawlPages}
                            onChange={(event) =>
                              setFormState((currentState) => ({
                                ...currentState,
                                maxCrawlPages: Math.max(1, Number(event.target.value)),
                              }))
                            }
                            className={`w-24 rounded-xl px-3 py-2 text-sm text-center outline-none transition ${inputClasses}`}
                          />
                        </div>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Standard: {DEFAULT_MAX_CRAWL_PAGES} · Slider bis {ADMIN_SLIDER_MAX} · Manuell unbegrenzt · Ab Seite {DEFAULT_MAX_CRAWL_PAGES + 1}: 0,5 s Verzögerung
                        </p>
                      </label>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={isCheckingPreflight || isRequestingVerification}
                  className="inline-flex min-w-[260px] items-center justify-center rounded-2xl bg-gradient-to-r from-slate-900 to-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_16px_36px_rgba(37,99,235,0.24)] transition hover:scale-[1.01] hover:shadow-[0_22px_40px_rgba(15,23,42,0.22)] disabled:cursor-wait disabled:opacity-70 disabled:hover:scale-100"
                >
                  {isCheckingPreflight || isRequestingVerification
                    ? isRescrapeMode ? 'Scraping wird geprüft...' : 'Angaben werden geprüft...'
                      : isRescrapeMode
                      ? 'Scraping erneut starten'
                      : emailVerification?.status === 'verified'
                        ? 'Setup mit bestätigter E-Mail starten'
                        : emailVerification
                          ? 'Bestätigungslink erneut senden'
                        : 'Angaben prüfen und Bestätigungslink senden'}
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-8">
              <div className="space-y-3 text-center">
                <div>
                  <h1 key={loadingPhraseIndex} className="text-3xl font-semibold tracking-tight" aria-live="polite">
                    {setupLoadingPhrases[loadingPhraseIndex]}
                  </h1>
                  <p className={`mt-2 text-sm ${mutedTextClasses}`}>
                    {isRescrapeMode
                      ? 'Sally aktualisiert gerade die Website-Grundlage für diesen Kunden.'
                      : 'Deine neue digitale Mitarbeiterin macht sich gerade mit deiner Website vertraut.'}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm font-medium">
                  <span className={mutedTextClasses}>Fortschritt</span>
                  <span>{progress}%</span>
                </div>
                <div className={`h-3 overflow-hidden rounded-full ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
                  <div
                    className="customize-progress-bar h-full rounded-full bg-slate-900"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="space-y-3">
                {stages.map((stage) => {
                  const isComplete = stage.status === 'complete';
                  const isActive = stage.status === 'active';

                  return (
                    <div
                      key={stage.label}
                      className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-sm ${
                        isComplete
                          ? isDarkMode
                            ? 'bg-emerald-500/15 text-emerald-200'
                            : 'bg-emerald-50 text-emerald-700'
                          : isActive
                            ? isDarkMode
                              ? 'bg-blue-500/15 text-blue-100'
                              : 'bg-blue-50 text-blue-700'
                            : isDarkMode
                              ? 'bg-slate-800/80 text-slate-400'
                              : 'bg-slate-50 text-slate-400'
                      }`}
                    >
                      {isComplete ? (
                        <CheckCircle2 className="h-5 w-5 shrink-0" />
                      ) : isActive ? (
                        <div className="customize-spinner h-5 w-5 shrink-0 rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <Circle className="h-5 w-5 shrink-0" />
                      )}
                      <span>{stage.label}</span>
                    </div>
                  );
                })}
              </div>

              {setupError ? <p className="whitespace-pre-line text-sm text-red-500">{setupError}</p> : null}
              {isWaitingForSetup ? (
                <p className={`text-sm ${mutedTextClasses}`}>Noch einen Moment, wir schließen die Einrichtung im Hintergrund ab.</p>
              ) : null}
              {isCompletingSetup ? (
                <p className={`text-sm ${mutedTextClasses}`}>Dashboard wird geöffnet...</p>
              ) : null}
              {setupStatus === 'success' && runtimeWarmupStatus === 'running' ? (
                <p className={`text-sm ${mutedTextClasses}`}>
                  Wir bereiten Avatar und Sprach-Verbindungen bereits im Hintergrund vor.
                </p>
              ) : null}
              {setupStatus === 'success' && runtimeWarmupStatus !== 'running' && runtimeWarmupMessage ? (
                <p className={`text-sm ${mutedTextClasses}`}>{runtimeWarmupMessage}</p>
              ) : null}

              <div className="flex justify-end">
                <button
                  type="button"
                  disabled
                  className="inline-flex min-w-[240px] cursor-not-allowed items-center justify-center rounded-2xl bg-slate-300 px-6 py-3 text-sm font-semibold text-white transition"
                >
                  {isCompletingSetup
                    ? 'Dashboard wird geöffnet...'
                    : isWaitingForSetup
                      ? 'Noch einen Moment...'
                      : setupStatus === 'error'
                        ? 'Setup fehlgeschlagen'
                        : 'Setup läuft...'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomizeFlow;
